import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Download, RefreshCw, AlertTriangle, Info, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { IRC_S2 } from "@/constants/testIds";

// Round 18 · IRC Form S2 (monthly remittance).
// Reads only from APPROVED pay periods. Values are never recomputed;
// the compute reads whatever `pay_line.calc.fortnightly_tax` and
// `pay_line.calc.taxable_gross` were stamped at approve time. See
// backend/irc_s2.py for the arithmetic and boxes/assumptions.

const MONTHS = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];

function defaultMonth() {
    // Default to the current month in the user's browser locale — the
    // operator will usually be filing a form for the month that just ended,
    // but the picker's right there so a one-click change is cheap.
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function monthOptions() {
    // 24 months back + 6 months forward so the picker covers historical
    // reprints AND a month the operator's about to open. Newest first.
    const out = [];
    const d = new Date();
    d.setDate(1);
    for (let n = -6; n < 24; n += 1) {
        const dd = new Date(d.getFullYear(), d.getMonth() - n, 1);
        const key = `${dd.getFullYear()}-${String(dd.getMonth() + 1).padStart(2, "0")}`;
        out.push({ value: key, label: `${MONTHS[dd.getMonth()]} ${dd.getFullYear()}` });
    }
    return out;
}

export default function IrcS2() {
    const [month, setMonth] = useState(defaultMonth());
    const [envelope, setEnvelope] = useState(null);
    const [loading, setLoading] = useState(false);
    const [supersedeOpen, setSupersedeOpen] = useState(false);
    const [supersedeReason, setSupersedeReason] = useState("");
    const options = useMemo(monthOptions, []);

    const load = async (m = month) => {
        setLoading(true);
        try {
            const { data } = await api.get(`/irc/s2/preview?month=${m}`);
            setEnvelope(data);
        } catch (ex) {
            toast.error(formatApiError(ex?.response?.data?.detail) || "Failed to load S2 preview");
            setEnvelope(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(month); /* eslint-disable-next-line */ }, [month]);

    const download = () => {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/irc/s2.pdf?month=${month}`;
        const token = localStorage.getItem("mss_token");
        fetch(url, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => { if (!r.ok) throw new Error(`${r.status}`); return r.blob(); })
            .then((blob) => {
                const a = document.createElement("a");
                a.href = URL.createObjectURL(blob);
                a.download = `irc_s2_${month}.pdf`;
                document.body.appendChild(a); a.click(); a.remove();
                URL.revokeObjectURL(a.href);
            })
            .catch((e) => toast.error(`Download failed (${e.message})`));
    };

    const boxes = envelope?.boxes;
    const per = envelope?.per_employee || [];
    const missing = envelope?.missing_header_fields || [];
    const incomplete = missing.length > 0;
    const lodgeable = envelope?.is_lodgeable;

    return (
        <div data-testid={IRC_S2.page} className="p-10 max-w-[1100px]">
            <div className="mb-8 flex items-start justify-between gap-6">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.24em] text-neutral-500 font-display">
                        Reports · Internal Revenue Commission
                    </div>
                    <h1 className="font-display font-bold text-4xl mt-1 text-[color:var(--kp-charcoal)]">
                        Form S2 · Monthly remittance
                    </h1>
                    <p className="text-sm text-neutral-500 mt-2 leading-relaxed max-w-[720px]">
                        Five boxes remitted to IRC every month. Values are pulled from stored pay-line
                        calculations — nothing is re-taxed here. Only APPROVED pay periods contribute.
                    </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <select
                        data-testid={IRC_S2.monthPicker}
                        value={month}
                        onChange={(e) => setMonth(e.target.value)}
                        className="kp-focus border border-neutral-300 px-3 py-2 text-[13px] bg-white rounded-[3px]"
                    >
                        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    <button
                        data-testid={IRC_S2.reloadBtn}
                        onClick={() => load(month)}
                        disabled={loading}
                        title="Reload"
                        className="p-2 border border-neutral-300 rounded-[3px] disabled:opacity-40"
                    >
                        <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
                    </button>
                    <button
                        data-testid={IRC_S2.downloadBtn}
                        onClick={download}
                        disabled={!envelope || loading}
                        className="inline-flex items-center gap-2 px-4 py-2 text-[12px] font-display font-semibold uppercase tracking-[0.12em] text-white rounded-[3px] disabled:opacity-40"
                        style={{ background: "var(--kp-red)" }}
                    >
                        <Download size={12} /> Download PDF
                    </button>
                    {envelope?.lodgement ? (
                        <button
                            data-testid={IRC_S2.supersedeBtn}
                            onClick={() => setSupersedeOpen(true)}
                            disabled={loading}
                            className="inline-flex items-center gap-2 px-3 py-2 text-[12px] font-display font-semibold uppercase tracking-[0.12em] border rounded-[3px] disabled:opacity-40"
                            style={{ borderColor: "var(--kp-red)", color: "var(--kp-red)" }}
                        >
                            Supersede
                        </button>
                    ) : (
                        <button
                            data-testid={IRC_S2.lodgeBtn}
                            onClick={async () => {
                                if (!lodgeable) { toast.error("Not lodgeable — fix header + approve periods first"); return; }
                                if (!window.confirm(`Mark ${month} S2 as lodged with IRC? This freezes the five boxes.`)) return;
                                try {
                                    await api.post(`/irc/s2/lodge`, { month });
                                    toast.success(`${month} S2 lodged`);
                                    load(month);
                                } catch (ex) { toast.error(formatApiError(ex?.response?.data?.detail) || "Lodge failed"); }
                            }}
                            disabled={!lodgeable || loading}
                            className="inline-flex items-center gap-2 px-3 py-2 text-[12px] font-display font-semibold uppercase tracking-[0.12em] border rounded-[3px] disabled:opacity-40"
                            style={{ borderColor: "var(--kp-charcoal)", color: "var(--kp-charcoal)" }}
                        >
                            <CheckCircle2 size={12} /> Mark as lodged
                        </button>
                    )}
                </div>
            </div>

            {/* Lodged badge + drift banner */}
            {envelope?.lodgement && (
                <div data-testid={IRC_S2.lodgedBadge}
                    className="mb-4 inline-flex items-center gap-2 px-3 py-1.5 text-[11px] font-display font-semibold uppercase tracking-[0.14em] rounded-[2px]"
                    style={{ background: "var(--kp-charcoal)", color: "white" }}>
                    <CheckCircle2 size={12} />
                    Lodged {envelope.lodgement.lodged_at?.slice(0, 10)} by {envelope.lodgement.lodged_by}
                    {envelope.lodgement.reopen_acknowledgements?.length > 0 && (
                        <span className="ml-2 opacity-70">
                            · {envelope.lodgement.reopen_acknowledgements.length} reopen ack{envelope.lodgement.reopen_acknowledgements.length > 1 ? "s" : ""}
                        </span>
                    )}
                </div>
            )}
            {envelope?.drift?.has_drift && (
                <div data-testid={IRC_S2.driftBanner}
                    className="mb-6 border-2 rounded-[3px] p-4"
                    style={{ borderColor: "var(--kp-red)", background: "rgba(227,6,19,0.08)", color: "#8B0F17" }}>
                    <div className="font-display font-bold uppercase tracking-[0.14em] text-[13px]">
                        <AlertTriangle size={14} className="inline mr-1" />
                        Drift · live data has moved since lodgement
                    </div>
                    <div className="mt-1 text-[12px]">
                        {envelope.drift.changed_period_ids.length > 0 && (
                            <div>Pay periods reopened after lodgement: <strong>{envelope.drift.changed_period_ids.length}</strong></div>
                        )}
                        {envelope.drift.box_50_delta_toea !== 0 && (
                            <div>SWT (box 50) moved by <strong className="font-mono-money">
                                {envelope.drift.box_50_delta_toea > 0 ? "+" : ""}
                                K{(envelope.drift.box_50_delta_toea / 100).toFixed(2)}
                            </strong></div>
                        )}
                        <div className="mt-1 opacity-90">
                            The boxes below show the LODGED snapshot (what IRC holds).
                            The S6 will show a reconcile break for this month until you unlodge + re-lodge.
                        </div>
                    </div>
                </div>
            )}

            {/* Header + status */}
            {envelope && (
                <div className="mb-6 grid grid-cols-2 gap-4">
                    <div className="border border-neutral-200 rounded-[3px] bg-white p-5">
                        <div className="text-[10px] uppercase tracking-[0.16em] text-neutral-500 font-display mb-2">
                            Employer (printed on form)
                        </div>
                        <HeaderField label="Employer name" value={envelope.header.employer_name} keyName="employer_name" />
                        <HeaderField label="TIN"           value={envelope.header.employer_tin}  keyName="employer_tin" />
                        <HeaderField label="Address"       value={envelope.header.employer_address} keyName="employer_address" />
                    </div>
                    <div className="border border-neutral-200 rounded-[3px] bg-white p-5">
                        <div className="text-[10px] uppercase tracking-[0.16em] text-neutral-500 font-display mb-2">
                            Coverage
                        </div>
                        <div className="text-[13px] text-[color:var(--kp-charcoal)] mb-1">
                            <span className="text-neutral-500 mr-2">Month</span>
                            {envelope.header.month_name} {envelope.header.year} <span className="text-neutral-400 font-mono-money ml-2">({envelope.header.month})</span>
                        </div>
                        <div className="text-[13px]">
                            <span className="text-neutral-500 mr-2">Approved fortnights</span>
                            <span data-testid={IRC_S2.includedPeriods} className="font-mono-money">
                                {envelope.included_periods.length ? envelope.included_periods.join(", ") : "— none in month"}
                            </span>
                        </div>
                        <div className="mt-3">
                            {lodgeable ? (
                                <span data-testid={IRC_S2.lodgeableBadge} className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-display font-semibold uppercase tracking-[0.14em] rounded-[2px]"
                                      style={{ background: "#E5F3EA", color: "var(--kp-success)" }}>
                                    <CheckCircle2 size={10} /> Complete · lodgeable
                                </span>
                            ) : (
                                <span data-testid={IRC_S2.lodgeableBadge} className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-display font-semibold uppercase tracking-[0.14em] rounded-[2px]"
                                      style={{ background: "#FFEDEE", color: "var(--kp-red)" }}>
                                    <AlertTriangle size={10} /> {missing.length ? "Incomplete header" : "Nothing to lodge"}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Incomplete banner */}
            {envelope && incomplete && (
                <div
                    data-testid={IRC_S2.incompleteBanner}
                    className="mb-6 border rounded-[3px] px-4 py-3 flex items-start gap-3 text-[13px]"
                    style={{ borderColor: "var(--kp-red)", background: "rgba(227,6,19,0.06)", color: "var(--kp-red)" }}
                >
                    <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                    <div>
                        <strong>Missing employer fields:</strong> {missing.join(", ")}. The PDF will still download,
                        but wears an <em>INCOMPLETE — do not lodge</em> watermark. Fix in
                        <a href="/settings/payroll" className="underline mx-1">Settings · Payroll rules</a>
                        under <em>Employer identification (for IRC forms)</em>.
                    </div>
                </div>
            )}

            {/* Round 21 · Supersede dialog. Reason is required (min 10 chars). */}
            {supersedeOpen && envelope?.lodgement && (
                <div data-testid={IRC_S2.supersedeDialog}
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <div className="bg-white rounded-[3px] w-full max-w-[540px] p-6">
                        <div className="text-[11px] uppercase tracking-[0.18em] font-display font-semibold mb-2"
                             style={{ color: "var(--kp-red)" }}>Supersede lodgement · {month}</div>
                        <p className="text-[13px] text-[color:var(--kp-charcoal)] mb-4 leading-relaxed">
                            Once a form is lodged with IRC it can't be un-lodged — the old snapshot is kept forever
                            with your reason. Superseding lets you lodge a corrected S2, but the audit trail records
                            the mistake. A written reason of at least <strong>10 characters</strong> is required.
                        </p>
                        <textarea data-testid={IRC_S2.supersedeReasonInput}
                            className="w-full border border-neutral-300 rounded-[3px] p-3 text-[13px] font-mono-money"
                            rows={3}
                            placeholder="e.g. wrong month selected; travel allowance missed one employee"
                            value={supersedeReason}
                            onChange={(e) => setSupersedeReason(e.target.value)} />
                        <div className="mt-1 text-[11px] text-neutral-500">
                            {supersedeReason.trim().length} / 10 minimum
                        </div>
                        <div className="mt-4 flex justify-end gap-2">
                            <button data-testid={IRC_S2.supersedeCancelBtn}
                                onClick={() => { setSupersedeOpen(false); setSupersedeReason(""); }}
                                className="px-4 py-2 text-[12px] font-display font-semibold uppercase tracking-[0.12em] border border-neutral-300 rounded-[3px]">
                                Cancel
                            </button>
                            <button data-testid={IRC_S2.supersedeConfirmBtn}
                                disabled={supersedeReason.trim().length < 10}
                                onClick={async () => {
                                    try {
                                        await api.post(`/irc/s2/lodgement/${month}/supersede`,
                                            { reason: supersedeReason.trim() });
                                        toast.success(`${month} lodgement superseded`);
                                        setSupersedeOpen(false); setSupersedeReason("");
                                        load(month);
                                    } catch (ex) {
                                        toast.error(formatApiError(ex?.response?.data?.detail) || "Supersede failed");
                                    }
                                }}
                                className="inline-flex items-center gap-2 px-4 py-2 text-[12px] font-display font-semibold uppercase tracking-[0.12em] text-white rounded-[3px] disabled:opacity-40"
                                style={{ background: "var(--kp-red)" }}>
                                Supersede
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Round 21 · Lodgement history (superseded docs kept forever). */}
            {envelope?.lodgement_history?.length > 0 && (
                <div data-testid={IRC_S2.historyList}
                    className="mb-6 border border-neutral-200 rounded-[3px] bg-white overflow-hidden">
                    <div className="px-5 py-3 border-b border-neutral-100 text-[13px] font-display font-semibold text-[color:var(--kp-charcoal)]">
                        Lodgement history · {envelope.lodgement_history.length}
                    </div>
                    <table className="w-full text-[12px]">
                        <thead>
                            <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-neutral-500 font-display" style={{ background: "#FAFAFA" }}>
                                <th className="px-4 py-2">Lodged</th>
                                <th className="px-4 py-2">By</th>
                                <th className="px-4 py-2 text-right">Box 50</th>
                                <th className="px-4 py-2">Status</th>
                                <th className="px-4 py-2">Reason (if superseded)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {envelope.lodgement_history.map((h) => (
                                <tr key={h.id} data-testid={IRC_S2.historyItem(h.id)}
                                    className="border-t border-neutral-100">
                                    <td className="px-4 py-2 font-mono-money">{h.lodged_at?.slice(0, 16).replace("T", " ")}</td>
                                    <td className="px-4 py-2">{h.lodged_by}</td>
                                    <td className="px-4 py-2 text-right font-mono-money">K{(h.box_50_toea / 100).toFixed(2)}</td>
                                    <td className="px-4 py-2">
                                        {h.superseded_at ? (
                                            <span className="text-[10px] uppercase tracking-[0.14em] font-display font-semibold px-1.5 py-0.5 rounded-[2px]"
                                                  style={{ background: "#FFF3D6", color: "#8A5B0F" }}>
                                                superseded · {h.superseded_at.slice(0, 10)}
                                            </span>
                                        ) : (
                                            <span className="text-[10px] uppercase tracking-[0.14em] font-display font-semibold px-1.5 py-0.5 rounded-[2px]"
                                                  style={{ background: "var(--kp-charcoal)", color: "white" }}>
                                                active
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-4 py-2 text-neutral-500 italic">{h.superseded_reason || "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Five boxes */}
            {boxes && (
                <div className="mb-6 border border-neutral-200 rounded-[3px] bg-white">
                    <div className="px-5 py-4 border-b border-neutral-100 text-[13px] font-display font-semibold text-[color:var(--kp-charcoal)]">
                        Remittance · the five boxes
                    </div>
                    <table className="w-full text-[13px]">
                        <tbody>
                            <BoxRow n={10} testid={IRC_S2.box10} label="Number of employees at month end"
                                    value={boxes.box_10_employees_at_month_end} unit="count"
                                    note="Distinct employees paid in the month." />
                            <BoxRow n={20} testid={IRC_S2.box20} label="Total gross wages · ALL employees"
                                    value={boxes.box_20_total_gross_kina} unit="kina"
                                    note="Includes travel allowance because it is grossed up and taxed." />
                            <BoxRow n={30} testid={IRC_S2.box30} label="Number of employees who had tax deducted"
                                    value={boxes.box_30_employees_with_swt} unit="count"
                                    note="Distinct employees whose TOTAL monthly SWT > 0." />
                            <BoxRow n={40} testid={IRC_S2.box40} label="Total gross wages · those employees only"
                                    value={boxes.box_40_gross_for_swt_employees_kina} unit="kina"
                                    note="Full monthly gross for employees in box 30, even fortnights they weren't taxed." />
                            <BoxRow n={50} testid={IRC_S2.box50} label="Total tax deducted · REMIT TO IRC"
                                    value={boxes.box_50_total_swt_kina} unit="kina" emphasise
                                    note="Sum of pay-line fortnightly_tax across the box-30 employees." />
                        </tbody>
                    </table>
                </div>
            )}

            {/* Assumptions */}
            {envelope?.assumptions?.length > 0 && (
                <div className="mb-6 border border-amber-200 rounded-[3px] p-4"
                     style={{ background: "#FFFAEA" }}>
                    <div className="flex items-center gap-2 mb-2 text-[12px] font-display font-semibold uppercase tracking-[0.14em]"
                         style={{ color: "#8A5B0F" }}>
                        <Info size={12} /> Assumptions · awaiting client answer
                    </div>
                    <ul className="space-y-1.5 text-[12px]" style={{ color: "#5D3F0A" }}>
                        {envelope.assumptions.map((a) => (
                            <li key={a.key} data-testid={IRC_S2.assumption(a.key)} className="flex gap-2">
                                <span style={{ color: "#B57800" }}>•</span>
                                <span>{a.text}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Audit table */}
            {per.length > 0 && (
                <div className="mb-6 border border-neutral-200 rounded-[3px] bg-white overflow-hidden">
                    <div className="px-5 py-4 border-b border-neutral-100 text-[13px] font-display font-semibold text-[color:var(--kp-charcoal)]">
                        Audit · per-employee breakdown (not printed on the form)
                    </div>
                    <table className="w-full text-[12px]">
                        <thead>
                            <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-neutral-500 font-display" style={{ background: "#FAFAFA" }}>
                                <th className="px-4 py-2">Employee</th>
                                <th className="px-4 py-2 text-right">Fortnights</th>
                                <th className="px-4 py-2 text-right">Monthly gross</th>
                                <th className="px-4 py-2 text-right">Monthly SWT</th>
                                <th className="px-4 py-2">In box 30/40?</th>
                            </tr>
                        </thead>
                        <tbody>
                            {per.map((r) => (
                                <tr key={r.employee_id}
                                    data-testid={IRC_S2.auditRow(r.employee_id)}
                                    className="border-t border-neutral-100">
                                    <td className="px-4 py-2 text-[color:var(--kp-charcoal)]">{r.name}</td>
                                    <td className="px-4 py-2 text-right font-mono-money">{r.fortnights}</td>
                                    <td className="px-4 py-2 text-right font-mono-money">{r.gross_kina}</td>
                                    <td className="px-4 py-2 text-right font-mono-money"
                                        style={{ color: r.tax_toea ? "var(--kp-red)" : "#8B8B90" }}>
                                        {r.tax_kina}
                                    </td>
                                    <td className="px-4 py-2 font-display uppercase text-[10px] tracking-[0.14em]"
                                        style={{ color: r.included_in_box_30_40 ? "var(--kp-success)" : "#8B8B90" }}>
                                        {r.included_in_box_30_40 ? "Yes" : "no"}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {envelope && envelope.per_employee.length === 0 && (
                <div className="p-8 text-center border border-dashed border-neutral-300 rounded-[3px] text-neutral-500 text-[13px]">
                    No approved pay periods in {envelope.header.month_name} {envelope.header.year}.
                </div>
            )}
        </div>
    );
}

function HeaderField({ label, value, keyName }) {
    const missing = value === null || value === undefined || value === "";
    return (
        <div className="text-[13px] mb-1 flex items-baseline gap-2">
            <span className="text-neutral-500 shrink-0" style={{ minWidth: 120 }}>{label}</span>
            {missing ? (
                <span className="font-mono-money" style={{ color: "var(--kp-red)", fontWeight: 600 }}>
                    Missing — set in Settings ({keyName})
                </span>
            ) : (
                <span className="text-[color:var(--kp-charcoal)]">{value}</span>
            )}
        </div>
    );
}

function BoxRow({ n, testid, label, value, unit, note, emphasise }) {
    return (
        <tr className="border-b border-neutral-100 last:border-b-0">
            <td className="px-5 py-3 w-[70px] text-center font-mono-money font-semibold text-[color:var(--kp-charcoal)]"
                style={{ fontSize: 15 }}>{n}</td>
            <td className="px-5 py-3">
                <div className="text-[color:var(--kp-charcoal)]">{label}</div>
                <div className="text-[11px] text-neutral-500 mt-0.5">{note}</div>
            </td>
            <td data-testid={testid} className="px-5 py-3 text-right font-mono-money"
                style={{ minWidth: 180, fontSize: emphasise ? 22 : 18,
                         fontWeight: 700,
                         color: emphasise ? "var(--kp-red)" : "var(--kp-charcoal)" }}>
                {unit === "count" ? value : value}
            </td>
        </tr>
    );
}
